import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { DistanceFilter } from './DistanceFilter';
import { AgeRangeFilter } from './AgeRangeFilter';
import { LocationFilter } from './LocationFilter';
import { useApplyFilter, useResetFilter } from '../hook/useFilterAction';
import {
  CommunityFilterStore,
  useFetchCommunityFilter,
} from '../hook/useCommunityFilter';
import { useCommunityFilterDispatch } from '../provider/CommunityFilterDispatchProvider';

export const ProfileFilterMenu = ({
  communityFilterStore,
}: {
  communityFilterStore: CommunityFilterStore;
}) => {
  const communityFilterDispatch = useCommunityFilterDispatch();
  const [searchLocation, setSearchLocation] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const changeLocation = () => setSearchLocation(true);
  const resetFilter = useResetFilter(communityFilterDispatch);
  const applyFilter = useApplyFilter(communityFilterStore.filters);
  const fetchCommunityFilter = useFetchCommunityFilter(communityFilterDispatch);
  const handleApplyFilter = () => {
    setAnchorEl(null);
    applyFilter();
  };

  useEffect(() => {
    fetchCommunityFilter();
  }, [fetchCommunityFilter]);

  const closeFilterMenu = useCallback(() => {
    setAnchorEl(null);
    setSearchLocation(false);
  }, []);

  const iconActiveStyles = {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
  };

  return (
    <ClickAwayListener onClickAway={closeFilterMenu}>
      <>
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            borderRadius: 3,
            ...(!!anchorEl && iconActiveStyles),
          }}
        >
          <FilterListIcon />
        </IconButton>
        <Popover
          anchorEl={anchorEl}
          id='account-menu'
          open={!!anchorEl}
          onClose={closeFilterMenu}
          PaperProps={{
            elevation: 0,
            sx: {
              minWidth: 400,
              borderRadius: 4,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 2.5,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {!searchLocation ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    px: 10,
                    py: 5,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
                    <FormattedMessage id='community.filter.title' />
                  </Typography>
                  <Button variant='text' onClick={resetFilter}>
                    <Typography sx={{ color: 'secondary.contrastText' }}>
                      <FormattedMessage id='common.reset' />
                    </Typography>
                  </Button>
                </Box>
                <Divider />
                <Box>
                  <DistanceFilter
                    changeLocation={changeLocation}
                    communityFilter={communityFilterStore.filters}
                  />
                  <Divider sx={{ mx: 5 }} />
                  <AgeRangeFilter
                    communityFilter={communityFilterStore.filters}
                  />
                  <Box sx={{ px: 10, mb: 5 }}>
                    <Button
                      onClick={handleApplyFilter}
                      disableElevation
                      fullWidth
                      variant='contained'
                      sx={{
                        borderRadius: 7.5,
                        py: 4.5,
                      }}
                    >
                      <FormattedMessage id='community.filter.apply' />
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <LocationFilter setSearchLocation={setSearchLocation} />
            )}
          </Box>
        </Popover>
      </>
    </ClickAwayListener>
  );
};
